import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Alert, Badge, Button, SectionCard, PageWrapper } from "@/deps/ui-library";
import { api } from "@/api";
import { useSession, useUsers } from "@/api/queries";
import { CYCLE_NAMES, PLAN_NAMES, trialDaysLeft } from "@/app/plan";
import { useCan } from "@/app/session";
import { useSubscription } from "@/app/subscription";
import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { BILLING_CONTACT } from "@/lib/billingContact";
import { formatDate } from "@/lib/dates";
import { saveFile } from "@/lib/download";
import { ManageSubscription } from "./ManageSubscription";

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-line-soft flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b px-4 py-2.5 text-sm last:border-0">
      <span className="text-fg-muted">{label}</span>
      <span className="text-fg">{children}</span>
    </div>
  );
}

/**
 * Settings › Subscription: the plan, what's on it, and its users, as RigoSub has them (suite ADR-0096). During a
 * trial an administrator subscribes from here (ADR-0097); changing a paid plan is still a mail to billing.
 */
export function SubscriptionPage() {
  const subscription = useSubscription();
  const status = subscription?.status;
  const can = useCan();
  const administrator = can("settings.subscription.manage");
  const { data: users } = useUsers(can("settings.users.manage"));
  const [exporting, setExporting] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const navigate = useNavigate();
  const demo = useSession().data?.organization.sampleData === true;

  if (demo) {
    return (
      <PageWrapper spacing="md" className="p-0">
        <PageHeader title="Subscription" />
        <EmptyState
          icon="FlaskConical"
          title="The demo company isn't part of a subscription"
          description="It's a sample company to try things in, and it's never billed. Your own company's plan and trial are in its Settings › Subscription."
        />
      </PageWrapper>
    );
  }

  if (!status?.enabled || !status.planCode) {
    return (
      <PageWrapper spacing="md" className="p-0">
        <PageHeader title="Subscription" />
        <EmptyState
          icon="CreditCard"
          title="No subscription to show"
          description="This deployment doesn't hold a subscription for the company, so nothing is limited by a plan."
        />
      </PageWrapper>
    );
  }

  const plan = PLAN_NAMES[status.planCode] ?? status.planCode;
  const seats =
    status.includedUsers != null ? status.includedUsers + (status.extraUsers ?? 0) : null;
  const inUse = users?.filter((user) => user.status !== "disabled").length;
  const reporting = status.addOns?.includes("REPORTING") ?? false;

  const exportBooks = async () => {
    setExporting(true);
    setRefused(null);
    try {
      saveFile(await api.subscription.exportData(), "rigo-accounting-books.zip");
    } catch (error) {
      setRefused(error instanceof Error ? error.message : "The books couldn't be exported.");
    } finally {
      setExporting(false);
    }
  };

  // A paid subscription's administrator manages it from RigoSub's account (suite ADR-0098).
  if (administrator && !status.trialEndsOn) {
    return (
      <PageWrapper spacing="md" className="p-0">
        <PageHeader
          title="Subscription"
          actions={
            <Button
              colorScheme="default"
              variant="secondary"
              size="sm"
              leftIcon="Download"
              loading={exporting}
              onClick={() => void exportBooks()}
            >
              Export data
            </Button>
          }
        />
        {refused ? (
          <Alert config={{ variant: "subtle", colorScheme: "danger" }} description={refused} />
        ) : null}
        <ManageSubscription inUse={inUse} actions={null} />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper spacing="md" className="p-0">
      <PageHeader
        title="Subscription"
        actions={
          administrator ? (
            <div className="flex flex-wrap gap-2">
              <Button
                colorScheme="default"
                variant="secondary"
                size="sm"
                leftIcon="Download"
                loading={exporting}
                onClick={() => void exportBooks()}
              >
                Export data
              </Button>
              {status.trialEndsOn ? (
                <Button
                  colorScheme="primary"
                  size="sm"
                  onClick={() => void navigate({ to: "/settings/subscription/checkout" })}
                >
                  Subscribe
                </Button>
              ) : null}
            </div>
          ) : null
        }
      />

      {refused ? (
        <Alert config={{ variant: "subtle", colorScheme: "danger" }} description={refused} />
      ) : null}

      <SectionCard title="Plan" flush>
        <Row label="Plan">
          <span className="flex items-center gap-2">
            {plan}
            {status.trialEndsOn ? (
              <Badge colorScheme="info" size="sm">
                Trial
              </Badge>
            ) : null}
          </span>
        </Row>
        {status.trialEndsOn ? (
          <Row label="Trial ends">
            {`${formatDate(status.trialEndsOn)} · ${trialDaysLeft(status.trialEndsOn)} days left`}
          </Row>
        ) : (
          <>
            {status.cycle ? (
              <Row label="Billed">{CYCLE_NAMES[status.cycle] ?? status.cycle}</Row>
            ) : null}
            {status.renewsOn ? <Row label="Renews">{formatDate(status.renewsOn)}</Row> : null}
          </>
        )}
      </SectionCard>

      <SectionCard title="Modules" flush>
        <Row label="Core accounting">{plan}</Row>
        <Row label="Reporting layer">{reporting ? "Added" : "Not added"}</Row>
      </SectionCard>

      {seats !== null ? (
        <SectionCard title="Users" flush>
          <Row label="Users">
            {inUse !== undefined ? `${inUse} of ${seats}` : `${seats} seats`}
            {` · ${status.includedUsers} included${status.extraUsers ? ` + ${status.extraUsers} extra` : ""}`}
          </Row>
        </SectionCard>
      ) : null}

      <Alert
        config={{ variant: "subtle", colorScheme: "default" }}
        description={
          administrator
            ? status.trialEndsOn
              ? "Subscribe to keep going after the trial. Everything from the trial is kept."
              : `To change the plan, or add users or the Reporting layer, write to ${BILLING_CONTACT.email}.`
            : "Ask an administrator to change the plan."
        }
      />
    </PageWrapper>
  );
}
